// src/pages/PracticeVerificationPending.jsx
//
// Shown when a firm's practice_number_status is 'pending_verification' —
// i.e. the practice number they registered with already matched another
// active firm. This page is the ONLY place documents are requested; the
// vast majority of signups never see it (see backend/main.py register_firm).
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { uploadPracticeNumberDocuments } from '../api/kailux'
import { Upload, CheckCircle2, Clock, Mail } from 'lucide-react'


import { T, alpha } from '../theme/theme'
export default function PracticeVerificationPending() {
  const { firm, user, loadFirm } = useAuth()
  const navigate = useNavigate()
  const [files,     setFiles]     = useState([])
  const [uploaded,  setUploaded]  = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error,     setError]     = useState('')

  const status = firm?.practice_number_status

  // Already resolved (approved) — no need to sit on this page.
  if (status && status !== 'pending_verification') {
    navigate('/dashboard')
    return null
  }

  const handleFiles = (e) => setFiles(Array.from(e.target.files || []))

  const handleUpload = async () => {
    if (!files.length) { setError('Please choose at least one file to upload.'); return }
    setUploading(true); setError('')
    try {
      await uploadPracticeNumberDocuments(firm.id, files)
      setUploaded(true)
      setFiles([])
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleRefresh = async () => {
    if (user?.id) await loadFirm(user.id)
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, color:T.tx,
      fontFamily:"'Outfit',sans-serif", display:'flex', alignItems:'center',
      justifyContent:'center', padding:'24px' }}>
      <div style={{ width:'100%', maxWidth:'520px', background:T.s1,
        border:`1px solid ${T.bd}`, borderRadius:'16px', padding:'32px' }}>

        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
          <Clock size={22} color={T.yl} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800' }}>
            Practice number verification
          </div>
        </div>

        <p style={{ fontSize:'14px', color:T.mt, lineHeight:'1.7', marginBottom:'20px' }}>
          Your account is live — you can explore KaiLux while this is pending. But the practice
          number you registered with is already associated with another firm on KaiLux, so we
          need a quick check before you can generate an AFS or management accounts.
        </p>

        {uploaded ? (
          <div style={{ background:T.gnd, border:`1px solid ${alpha(T.gn, 0.35)}`,
            borderRadius:'10px', padding:'16px', display:'flex', gap:'10px',
            alignItems:'flex-start', marginBottom:'20px' }}>
            <CheckCircle2 size={18} color={T.gn} style={{ flexShrink:0, marginTop:'2px' }} />
            <div style={{ fontSize:'13px', color:T.tx, lineHeight:'1.6' }}>
              Documents received. Our team will review and email you once it's resolved —
              usually within one business day.
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize:'11px', fontWeight:'700', color:T.lt,
              letterSpacing:'1px', marginBottom:'8px' }}>
              UPLOAD SUPPORTING DOCUMENTS
            </div>
            <div style={{ fontSize:'13px', color:T.mt, marginBottom:'12px', lineHeight:'1.6' }}>
              e.g. your SAICA, SAIPA or IRBA registration certificate, or any document that
              confirms this practice number belongs to your firm.
            </div>
            <label style={{ display:'flex', alignItems:'center', justifyContent:'center',
              gap:'8px', padding:'20px', border:`1.5px dashed ${T.bd}`, borderRadius:'10px',
              cursor:'pointer', background:T.s3, marginBottom:'12px' }}>
              <Upload size={16} color={T.mt} />
              <span style={{ fontSize:'13px', color:T.mt }}>
                {files.length ? `${files.length} file(s) selected` : 'Choose files (PDF, JPG, PNG)'}
              </span>
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFiles} style={{ display:'none' }} />
            </label>

            {error && <div style={{ fontSize:'13px', color:T.rd, marginBottom:'12px' }}>{error}</div>}

            <button onClick={handleUpload} disabled={uploading}
              style={{ width:'100%', padding:'12px', borderRadius:'8px', border:'none',
                background: uploading ? T.mt : T.prg,
                color:T.onpri, fontSize:'14px', fontWeight:'700',
                cursor: uploading ? 'not-allowed' : 'pointer' }}>
              {uploading ? 'Uploading…' : 'Upload documents'}
            </button>
          </>
        )}

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          marginTop:'22px', paddingTop:'18px', borderTop:`1px solid ${T.bd}` }}>
          <button onClick={handleRefresh}
            style={{ background:'none', border:'none', color:T.mt, fontSize:'13px', cursor:'pointer' }}>
            Check status
          </button>
          <button onClick={() => navigate('/dashboard')}
            style={{ background:'none', border:'none', color:T.or, fontSize:'13px',
              fontWeight:'600', cursor:'pointer' }}>
            Continue exploring KaiLux →
          </button>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'16px' }}>
          <Mail size={13} color={T.mt} />
          <span style={{ fontSize:'12px', color:T.mt }}>
            Questions? <a href="mailto:support@kailux.co.za" style={{ color:T.or }}>support@kailux.co.za</a>
          </span>
        </div>
      </div>
    </div>
  )
}
